import React, {useCallback, useEffect, useState} from "react";
import {Clip, Highlight, SocialClipsUrl, SocialPublication} from "../../types/clipTypes";
import StyledPopup from "../CommonComponents/StyledPopup/StyledPopup";
import {Box, Typography} from "@mui/material";
import {useTheme} from "../../contexts/theme";
import VideoEditorPopupHeader from "./EditorPopupHeader";
import ModeSelector from "./ModesSelector";
import {AspectRatio, LoadError} from "../../types/generic";
import WindowPlayer from "../CommonComponents/WindowPlayer/WindowPlayer";
import VideoPlayerBar from "./VideoPlayerBar";
import VideoPlayerControls from "./VideoPlayerControls";
import {TrimPoint, Landmark, FocusElement} from "../../types/editor";
import {getEditCoordinates, moveTrimPoint} from "../../utils/utils";
import {
    changeMode,
    editLandMark,
    getBaseState,
    save,
    saveTrimPoints, socialEdit,
    startEditing
} from "../../queries/editorQueries";
import _ from 'lodash';
import SocialPublish from "../SocialPublish/SocialPublish";
import SpinnerBox from "../CommonComponents/SpinnerBox/SpinnerBox";
import ErrorSnackBar from "../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import LoopIcon from "@mui/icons-material/Loop";

interface VideoEditorPopupProps {
    clip?: Clip | Highlight;
    isOpen: boolean;
    handleClose: () => void;
    setIsClipDownloading?: (clipState: string) => void;
    setExternalError:(error: LoadError) => void;
    contentType?: 'clip' | 'highlight'
}

interface WindowMode {
    label: string;
    key: string;
    disabled: boolean;
}

interface BaseState {
    coordinates: number[],
    trimPoints: TrimPoint[]
}

interface ServerLandMark{
    coordinate: number,
    frame: number
}


const grabberDefaultLength = 10
// {label:'9:16', disabled: false, numeric: 9/16}, {label:'1:1', disabled: false, numeric: 1}]
const defaultAspectRatio = {
    label: '16:9',
    numeric: 16/9,
    disabled: false
}


const VideoEditorPopup: React.FC<VideoEditorPopupProps> = ({setExternalError, isOpen, handleClose, clip, setIsClipDownloading, contentType = 'clip'}) => {

    const theme = useTheme();
    const [selectedAspectRatio, setSelectedAspectRatio] = React.useState<AspectRatio>({
        label: '16:9',
        disabled: false,
        numeric: 16 / 9
    });
    const [videoTime, setVideoTime] = React.useState<number>(0);
    const [videoDuration, setVideoDuration] = React.useState<number>(0);
    const [trimPoints, setTrimPoints] = React.useState<TrimPoint[]>([]);
    const [platFormLandmarks, setPlatFormLandmarks] = React.useState<any[]>([]);
    const [landmarks, setLandmarks] = React.useState<Landmark[]>([]);
    const [isActiveTime, setIsActiveTime] = React.useState<boolean>(false);
    const [availableWindowModes, setAvailableWindowModes] = React.useState<WindowMode[]>([{key: 'auto', disabled: true, label: 'Auto'}, {key: 'manual', disabled: false, label: 'Manual'}])
    const [selectedWindowMode, setSelectedWindowMode] = React.useState<WindowMode>({label: 'Manual', key: 'manual', disabled: false});

    const [changeVideoTime, setChangeVideoTime] = React.useState<number>(0);
    const [isPlaying, setIsPlaying] = React.useState<boolean>(false);
    const [muted, setMuted] = React.useState<boolean>(true);
    const [deleteMode, setDeleteMode] = React.useState<boolean>(false);
    const [windowCoords, setWindowCoords] = React.useState<number[]>([]);
    const [isGrabberDragging, setIsGrabberDragging] = React.useState<boolean>(false);
    const [currentFocus, setCurrentFocus] = React.useState<FocusElement>({type: '', index: -1});
    const [videoRef, setVideoRef] = React.useState<HTMLVideoElement>();
    const [editSessionId, setEditSessionId] = React.useState<string>('');

    const trimPointsRef = React.useRef<TrimPoint[]>([]);
    const videoDurationRef = React.useRef<number>(0);

    const [loadingInitialState, setLoadingInitialState] = React.useState<boolean>(true);
    const [availableFormats, setAvailableFormats] = React.useState<AspectRatio[]>([]);
    const [editingBaseState, setEditingBaseState] = React.useState<BaseState>();
    const [isWindowUpdating, setIsWindowUpdating] = React.useState<boolean>(false);

    const [isPublishOpen, setIsPublishOpen] = React.useState<boolean>(false);
    const [socialClipUrls, setSocialClipUrls] = React.useState<SocialClipsUrl>({});
    const [socialPublications, setSocialPublications] = React.useState<SocialPublication>({'facebook': [], 'instagram':[], 'youtube':[]});

    const [windowUpdated, setWindowUpdated] = React.useState(false)
    const [trimPointsUpdated, setTrimPointsUpdated] = React.useState(false)

    const [loadError, setLoadError] = useState<LoadError>({message: '', error: false});
    const [savingState, setSavingState] = React.useState<boolean>(false);

    useEffect(() => {
        if (isOpen){
            setLoadingInitialState(true);
        }
    }, [isOpen]);

    function handleAspectRatioChange(aspectRatio: AspectRatio): void {
        setSelectedAspectRatio(aspectRatio);
    }

    function onVideoPlay(){
        if (!isPlaying) {
            if (trimPointsRef.current && trimPointsRef.current.length > 0) {
                const lastPossibleTrimPoints = trimPointsRef.current[trimPointsRef.current.length - 1];
                const endTime = Math.trunc(lastPossibleTrimPoints.end * 1000) / 1000;
                const startTime = trimPointsRef.current[0].start
                const truncVideoTime = Math.trunc(videoTime * 1000) / 1000;
                if (endTime === truncVideoTime) {
                    updateCurrentTime(startTime)
                }
            }
        }
        setIsPlaying(true);
    }

    async function handleWindowModeChange(mode: WindowMode) {
        try{
            setSelectedWindowMode(mode);
            setIsWindowUpdating(true);
            const editState = await changeMode(editSessionId, mode.key)
            setWindowCoords(editState.coordinates);
            setWindowUpdated(true)
            setTrimPoints(editState.trim_points);
            setTrimPointsUpdated(true)
            setLandmarks(editState.landmarks);
        }catch (e) {
            setLoadError({message: 'Error changing edit mode', error: true})
        }finally {
            setIsWindowUpdating(false);
        }

    }

    const debouncedEditTrimPoints = useCallback(
        _.debounce((clipId: string, trimPoints: TrimPoint[]) => saveTrimPoints(clipId, trimPoints), 250),
        []
    );

    const debouncedEditLandMark = useCallback(
        _.debounce(async (editSessionId: string, landmark: Landmark, operation: string) => {
            setIsWindowUpdating(true)
            const newCoords = await editLandMark(editSessionId, landmark, operation);
            setWindowCoords(newCoords);
            setWindowUpdated(true)
            setIsWindowUpdating(false)
        }, 1000),
        []
    );


    function updateTrimPoints(trimPoints: TrimPoint[], time: number): void {
        setTrimPoints(trimPoints);
        setTrimPointsUpdated(true)
        updateCurrentTime(time);
        setIsPlaying(false);
        debouncedEditTrimPoints(editSessionId, trimPoints)
    }

    function mergeTrimPoints(index1: number, index2: number) {
        const tempTrimPoints = [...trimPointsRef.current];
        if (tempTrimPoints[index1] && tempTrimPoints[index2]) {
            const newTrimPoint: TrimPoint = {start: tempTrimPoints[index1].start, end: tempTrimPoints[index2].end};
            tempTrimPoints.splice(index1, 1);
            tempTrimPoints.splice(index2 - 1, 1);
            tempTrimPoints.push(newTrimPoint);
            tempTrimPoints.sort((a, b) => a.start - b.start)
            setTrimPoints(tempTrimPoints);
            setTrimPointsUpdated(true)
        }
    }

    function moveTrimPoints(amount: number, direction: number, index: number, type: 'start' | 'end', shiftKeyPressed: boolean): void {
        const tempTrimPoints = [...trimPointsRef.current];
        if (tempTrimPoints[index]) {
            const time = tempTrimPoints[index][type] + (amount * direction);
            moveTrimPoint(tempTrimPoints, time, videoDurationRef.current, index, type, deleteTrimPoint, updateTrimPoints, mergeTrimPoints, {
                use: !shiftKeyPressed,
                shots: clip && clip.shots ? clip.shots : []
            });
        }
    }

    function addTrimPoint(trimPoint: TrimPoint): void {
        const tempTrimPoints = [...trimPoints, trimPoint];
        tempTrimPoints.sort((a, b) => a.start - b.start)
        setTrimPoints(tempTrimPoints);
        setTrimPointsUpdated(true)
        debouncedEditTrimPoints(editSessionId, tempTrimPoints);
    }

    function updateCurrentTime(time: number): void {
        setChangeVideoTime(time);
        setVideoTime(time);
    }

    function deleteTrimPoint(grabberIndex: number): void {
        const tempTrimPoints = [...trimPoints];
        tempTrimPoints.splice(grabberIndex, 1);
        setTrimPoints(tempTrimPoints);
        setTrimPointsUpdated(true)
        setDeleteMode(false);
        debouncedEditTrimPoints(editSessionId, tempTrimPoints);
    }

    function checkAlreadyPresentTrimPoint(time: number, minTrimPointDistance: number) {
        for (const tp of trimPoints){
            if (time <= tp.start + minTrimPointDistance && time >= tp.start - minTrimPointDistance){
                return {presence: true, side: 'start'}
            }
            if (time <= tp.end + minTrimPointDistance && time >= tp.end - minTrimPointDistance){
                return {presence: true, side: 'end'}
            }
        }
        return {presence: false, side: null};
    }

    function addGrabber(): void {
        const activeTrimPointIndex = getActiveTrimPointIndex(videoTime)
        const alreadyPresentTrimPoint = checkAlreadyPresentTrimPoint(videoTime, 1 / clip!.frame_rate)
        if (activeTrimPointIndex >= 0 && !alreadyPresentTrimPoint.presence) {
            const tempTrimPoints = [...trimPoints]
            const newTrimPoint = {start: videoTime + 1 / clip!.frame_rate, end: tempTrimPoints[activeTrimPointIndex].end}
            tempTrimPoints[activeTrimPointIndex].end = videoTime;
            tempTrimPoints.splice(activeTrimPointIndex + 1, 0, newTrimPoint);
            setTrimPoints(tempTrimPoints);
            setTrimPointsUpdated(true)
            debouncedEditTrimPoints(editSessionId, tempTrimPoints);
        } else if(activeTrimPointIndex >= 0 && alreadyPresentTrimPoint.presence){
            const tempTrimPoints = [...trimPoints]
            tempTrimPoints.sort((a, b) => a.start - b.start)
            if (alreadyPresentTrimPoint.side ==='end'){
                const newStart = videoTime + 1 / clip!.frame_rate
                let newEnd = newStart + videoDuration / grabberDefaultLength

                const overlapEndTrimPoint = trimPoints.find(tp => newEnd  >= tp.start && newStart <= tp.start)
                if (overlapEndTrimPoint){
                    newEnd = overlapEndTrimPoint.start - 1/clip!.frame_rate
                }
                const newTrimPoint = {start: newStart <= videoDuration ? newStart: videoDuration, end: newEnd <= videoDuration ? newEnd : videoDuration }
                if ((newTrimPoint.start !== videoDuration || newTrimPoint.end !== videoDuration) && newTrimPoint.end - newTrimPoint.start > 10/clip!.frame_rate){
                    tempTrimPoints.push(newTrimPoint)
                    tempTrimPoints.sort((a, b) => a.start - b.start)
                    setTrimPoints(tempTrimPoints);
                    setTrimPointsUpdated(true)
                    debouncedEditTrimPoints(editSessionId, tempTrimPoints);
                }
            }else{
                const newEnd = videoTime - 1 / clip!.frame_rate
                let newStart = newEnd - videoDuration / grabberDefaultLength
                const overlapEndTrimPoint = trimPoints.find(tp => newStart <= tp.end && newEnd >= tp.start)
                if (overlapEndTrimPoint){
                    newStart = overlapEndTrimPoint.end + 1/clip!.frame_rate
                }
                const newTrimPoint = {start: newStart >= 0 ? newStart: 0, end: newEnd >= 0 ? newEnd : 0 }
                if ((newTrimPoint.start !== 0 || newTrimPoint.end !== 0) && newTrimPoint.end - newTrimPoint.start > 10/clip!.frame_rate){
                    tempTrimPoints.push(newTrimPoint)
                    tempTrimPoints.sort((a, b) => a.start - b.start)
                    setTrimPoints(tempTrimPoints);
                    setTrimPointsUpdated(true)
                    debouncedEditTrimPoints(editSessionId, tempTrimPoints);
                }
            }
        } else {
            const intervalLength = videoDuration / grabberDefaultLength;
            const start = videoTime - intervalLength / 2 > 0 ? videoTime - intervalLength / 2 : 0;
            const end = videoTime + intervalLength / 2 < videoDuration ? videoTime + intervalLength / 2 : videoDuration;
            const newTrimPoint = {start, end}
            addTrimPoint(newTrimPoint)
        }
    }

    function addLandmark(landmark: Landmark) {
        if (!landmarks.find(lm => lm.time === landmark.time)) {
            setLandmarks([...landmarks, landmark]);
            debouncedEditLandMark(editSessionId, landmark, 'update');
        }else{
            debouncedEditLandMark(editSessionId, landmark, 'update');
        }
    }

    function deleteLandmark(landmarkIndex: number) {
        if (!isWindowUpdating){
            const tempLandMarks = [...landmarks];
            debouncedEditLandMark(editSessionId, tempLandMarks[landmarkIndex], 'delete');
            tempLandMarks.splice(landmarkIndex, 1);
            setLandmarks(tempLandMarks);
        }
    }

    function getActiveTrimPointIndex(time: number): number {
        for (let i = 0; i < trimPoints.length; i++) {
            const trimPoint = trimPoints[i];
            if (time >= trimPoint.start && time <= trimPoint.end) {
                return i
            }
        }
        return -1
    }

    function handleSkip(amount: number, direction: 1 | -1) {
        setVideoTime(prevTime => {
            let time = prevTime + (amount) * direction
            time = time < 0 ? 0 : time;
            time = time > videoDuration ? videoDuration : time;
            setChangeVideoTime(time);
            return time
        });
    }

    function changeFocusNoShortCut() {
        setCurrentFocus({type: 'noShortCut', index: -1})
    }

    useEffect(() => {
        if (isGrabberDragging) {
            setIsActiveTime(true);
        }else if (trimPoints.length === 0) {
            setIsActiveTime(true);
        }
        else {
            if (trimPointsRef.current){
                const activeTrimPointIndex = getActiveTrimPointIndex(videoTime);
                const currentIsActiveTime = activeTrimPointIndex >= 0;
                const isLastTrimPoint = activeTrimPointIndex === trimPointsRef.current.length - 1;
                const activeTrimPoint = trimPointsRef.current[activeTrimPointIndex];
                const currentFrame = Math.floor(videoTime*clip!.frame_rate);

                const lastTrimPointTime = trimPoints[trimPoints.length-1].end
                if (videoTime > lastTrimPointTime){
                    setIsPlaying(false);
                    setVideoTime(lastTrimPointTime);
                    setChangeVideoTime(lastTrimPointTime)
                }
                if (isLastTrimPoint && isPlaying && currentIsActiveTime && activeTrimPoint){
                    const lastFrame = Math.floor(activeTrimPoint.end*clip!.frame_rate);
                    if (currentFrame === lastFrame){
                        setIsPlaying(false);
                    }
                }
                if (!isLastTrimPoint && isPlaying && currentIsActiveTime && activeTrimPoint){
                    const lastTrimPointFrame = Math.floor(activeTrimPoint.end*clip!.frame_rate) - 1;
                    if (currentFrame === lastTrimPointFrame){
                        const nextTrimPoint = trimPointsRef.current[activeTrimPointIndex + 1];
                        const newVideoTime = nextTrimPoint.start + 1/clip!.frame_rate;
                        setVideoTime(newVideoTime);
                        setChangeVideoTime(newVideoTime)
                    }
                }
                setIsActiveTime(currentIsActiveTime)
            }
        }

    }, [videoTime, trimPoints]);

    function resetFocus() {
        setCurrentFocus({type: '', index: -1});
    }

    async function reset() {

        if (selectedWindowMode.key !== 'auto'){
            setSelectedWindowMode({label: 'Auto', key: 'auto', disabled: false})
            changeMode(editSessionId, 'auto')
        }
        setTrimPoints(editingBaseState!.trimPoints);
        setTrimPointsUpdated(true)
        debouncedEditTrimPoints(editSessionId, editingBaseState!.trimPoints);
        setWindowCoords(editingBaseState!.coordinates);
        setWindowUpdated(true)
        for(const landmark of landmarks){
            await (debouncedEditLandMark(editSessionId, landmark, 'delete'));
        }
        setLandmarks([]);
        if (trimPointsRef.current && trimPointsRef.current.length){
            updateCurrentTime(trimPointsRef.current[0].start)
        }else{
            updateCurrentTime(0);
        }

    }

    function captureSnapshot() {
        if (videoRef && clip) {
            const canvas = document.createElement("canvas");
            canvas.width = videoRef.videoWidth;
            canvas.height = videoRef.videoHeight;
            canvas!.getContext('2d')!.drawImage(videoRef, 0, 0, canvas.width, canvas.height);
            const dataURL = canvas.toDataURL();
            const a = document.createElement("a");
            a.href = dataURL;
            a.download = clip.src || clip.cloudfront_signed_url + ".png";
            a.click();
        }
    }

    async function onPostClick(){
        try{
            const editStateId = await save(editSessionId);
            const tempSocialUrls = {...socialClipUrls}
            setIsPublishOpen(true)
            for(let i = 0; i < availableFormats.length; i++){
                const aspectRatio = availableFormats[i];
                tempSocialUrls[aspectRatio.label] = await socialEdit(editStateId, aspectRatio.label);
                setSocialClipUrls(JSON.parse(JSON.stringify(tempSocialUrls)));
            }
        }catch (error){
            setLoadError({error: true, message: 'Error to produce social clips'});
            setIsPublishOpen(false);
        }

    }

    function onDeleteSocialPublication(social: string, postId: string){
        const tempSocialPublications = {...socialPublications}
        const selectedSocialPublications = socialPublications[social]
        if (selectedSocialPublications){
            const deleteIndex = selectedSocialPublications.findIndex(post => post.postId === postId)
            if (deleteIndex >= 0){
                selectedSocialPublications.splice(deleteIndex, 1);
            }
        }
        setSocialPublications(tempSocialPublications)
    }

    async function saveEditorState(){

        setTimeout(async () => {
            try{
                setSavingState(true)
                const editStateId = await save(editSessionId);
                const promiseArray: Promise<any>[] = []
                if (trimPointsUpdated){
                    await socialEdit(editStateId, '16:9',true)
                }
                setSavingState(false)
                if (windowUpdated || trimPointsUpdated){
                    for(let i = 0; i < availableFormats.length; i++){
                        const aspectRatio = availableFormats[i];
                        if(aspectRatio.label !== '16:9'){
                            promiseArray.push(socialEdit(editStateId, aspectRatio.label,true))
                        }
                    }
                }
                Promise.all(promiseArray)
                handlePopupClose();
            }catch (e){
                setSavingState(false)
                setLoadError({error: true, message: 'Error saving editor state'});
            }
        }, 250)

    }


    useEffect(() => {
        trimPointsRef.current = trimPoints;
    }, [trimPoints]);

    useEffect(() => {
        videoDurationRef.current = videoDuration;
    }, [videoDuration]);


    useEffect(() => {
        const loadInitialState = async () => {
            if (isOpen){
                try{
                    const [initialState, baseState] = await Promise.all([startEditing(clip!.id), getBaseState(clip!.id)]);
                    setTrimPoints(initialState.trim_points);
                    updateCurrentTime(initialState.trim_points[0] ? initialState.trim_points[0].start : 0);
                    setAvailableFormats(initialState.available_formats);
                    setWindowCoords(initialState.coordinates);
                    setEditSessionId(initialState.id);
                    setEditingBaseState({coordinates: baseState.coordinates, trimPoints: baseState.trim_points});
                    setPlatFormLandmarks(initialState.landmarks);
                    setSelectedWindowMode(initialState.settings && initialState.settings.mode === 'manual' ? {key: 'manual', disabled: false, label: 'Manual'} : {key: 'auto', disabled: false, label: 'Auto'})
                    if(initialState.auto_coordinates_available){
                        setAvailableWindowModes([{key: 'auto', disabled: false, label: 'Auto'}, {key: 'manual', disabled: false, label: 'Manual'}]);
                    }
                }catch(error){
                    console.error('Editing Error', error);
                    setExternalError({error: true, message: 'Error while starting the editing session'});
                    handlePopupClose();
                }finally {
                    if (isOpen){
                        setLoadingInitialState(false);
                    }
                }
            }
        }
        loadInitialState();
    }, [isOpen]);

    useEffect(() => {
        if (videoDuration > 0){
            const tempLandMarks = platFormLandmarks.map((serverLandmark: ServerLandMark): Landmark => {
                return {
                    time: serverLandmark.frame/clip!.frame_rate,
                    frame: serverLandmark.frame,
                    coordinate: serverLandmark.coordinate,
                    offset: (serverLandmark.frame/clip!.frame_rate) / videoDuration
                }
            })
            setLandmarks(tempLandMarks);
        }
    }, [videoDuration]);

    function handlePopupClose(){
        setTrimPoints([]);
        setWindowCoords([]);
        setLandmarks([]);
        setVideoDuration(0);
        setIsPlaying(false);
        setVideoTime(0);
        setSelectedAspectRatio({
            label: '16:9',
            disabled: false,
            numeric: 16 / 9
        });
        setIsPublishOpen(false);
        setSocialClipUrls({});
        setAvailableWindowModes([{key: 'auto', disabled: true, label: 'Auto'}, {key: 'manual', disabled: false, label: 'Manual'}]);
        setWindowUpdated(false)
        setTrimPointsUpdated(false)
        handleClose();
    }

    return (
        <>
            {clip &&
                <StyledPopup
                    width={'60vw'}
                    isOpen={isOpen}
                    onClose={handlePopupClose}
                    title={!loadingInitialState && !isPublishOpen ? <VideoEditorPopupHeader trimPoints={trimPoints} title={"Edit & Post"} clip={clip}/> : <></>
                }>
                    {loadingInitialState && !isPublishOpen &&
                        <SpinnerBox height={'80vh'} text={'Editor Loading'}/>
                    }
                    {!loadingInitialState && !isPublishOpen &&
                        <Box sx={{overflowX: 'hidden'}}>
                            <Box width={'100%'} marginBottom={theme.spacing.sm} marginTop={theme.spacing.sm}
                                 display='flex' justifyContent='space-between' alignItems={'center'}>
                                <ModeSelector onBlur={resetFocus} onFocus={changeFocusNoShortCut}
                                              modes={availableFormats} onModeSelection={handleAspectRatioChange}
                                              selected={selectedAspectRatio}/>
                                {isWindowUpdating && <Box display={'flex'} alignItems={'center'} gap={theme.spacing.xs}>
                                    <LoopIcon
                                        style={{animation: "spin 1s linear infinite"}}
                                        sx={{color: theme.palette.white, marginLeft: theme.spacing.sm}}
                                    />
                                    <Typography sx={{color:theme.palette.white}}>Computing Window</Typography>
                                </Box>}
                                <ModeSelector onBlur={resetFocus} onFocus={changeFocusNoShortCut}
                                              modes={availableWindowModes} onModeSelection={handleWindowModeChange}
                                              selected={selectedWindowMode}/>
                            </Box>
                            <Box width={'100%'} marginTop={theme.spacing.md} sx={{overflowY: 'hidden'}}>
                                <WindowPlayer
                                    setVideoDuration={setVideoDuration}
                                    frameRate={clip.frame_rate ? clip.frame_rate : 25}
                                    setVideoTime={setVideoTime}
                                    url={clip.src || clip.cloudfront_signed_url}
                                    changeVideoTime={changeVideoTime}
                                    isPlaying={isPlaying}
                                    muted={muted}
                                    windowCoords={windowCoords}
                                    aspectRatio={selectedAspectRatio}
                                    isActiveTime={isActiveTime}
                                    addLandMark={addLandmark}
                                    currentFocusElement={currentFocus}
                                    setIsPlaying={setIsPlaying}
                                    moveTrimPoint={moveTrimPoints}
                                    setVideoRef={setVideoRef}
                                    isWindowUpdating={isWindowUpdating}
                                    trimPoints={trimPoints}
                                    shortcuts={true}
                                    dispose={!isOpen}
                                />
                            </Box>
                            <Box width={'100%'} marginTop={theme.spacing.md}>
                                <VideoPlayerBar
                                    setVideoCurrentDuration={updateCurrentTime}
                                    videoCurrentTime={videoTime}
                                    totalVideoTime={videoDuration}
                                    trimPoints={trimPoints}
                                    updateTrimPoint={updateTrimPoints}
                                    deleteMode={deleteMode}
                                    onDeleteGrabber={deleteTrimPoint}
                                    landmarks={landmarks}
                                    onLandmarkClick={deleteLandmark}
                                    setIsGrabberDragging={setIsGrabberDragging}
                                    setCurrentFocus={setCurrentFocus}
                                    onBlur={resetFocus}
                                    mergeTrimPoints={mergeTrimPoints}
                                    shots={clip.shots ? clip.shots : []}
                                />
                            </Box>
                            <Box width={'100%'} marginTop={theme.spacing.md}>
                                {<VideoPlayerControls
                                    onPause={() => setIsPlaying(false)}
                                    onPlay={onVideoPlay}
                                    onMute={(muted) => setMuted(muted)}
                                    onAddGrabber={() => addGrabber()}
                                    onDeleteModeActivation={() => setDeleteMode(true)}
                                    videoTime={videoTime}
                                    isPlaying={isPlaying}
                                    muted={muted}
                                    onSkip={(amount) => handleSkip(amount, 1)}
                                    onRewind={(amount) => handleSkip(amount, -1)}
                                    onFocus={changeFocusNoShortCut}
                                    onBlur={resetFocus}
                                    onReset={reset}
                                    aspectRatios={availableFormats}
                                    onScreenShot={captureSnapshot}
                                    onDone={saveEditorState}
                                    isActiveTime = {isActiveTime}
                                    isWindowUpdating={isWindowUpdating}
                                    onPost={() => onPostClick()}
                                    selectedAspectRatio={selectedAspectRatio.label}
                                    clip={clip}
                                    setIsClipDownloading={setIsClipDownloading}
                                    editSessionId={editSessionId}
                                    handlePopupClose={handlePopupClose}
                                    doneDisabled={savingState}
                                />}
                            </Box>
                        </Box>
                    }
                    {isPublishOpen && <SocialPublish
                        clip={clip}
                        aspectRatios={availableFormats}
                        coordinates={getEditCoordinates(trimPoints, windowCoords, clip.frame_rate ? clip.frame_rate : 0)}
                        socialClips={socialClipUrls}
                        defaultAspectRatio={defaultAspectRatio}
                    />}
                    <ErrorSnackBar autoHideDuration={5000} open={loadError.error} setClose={() => setLoadError({message:'', error: false})} errorMessage={loadError.message}/>
                </StyledPopup>
            }
        </>
    );

}

export default VideoEditorPopup;